import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Utensils,
  Upload,
  FileText,
  Image,
  Download,
  Trash2,
  Calendar,
  User,
  Check,
  X,
  RefreshCw,
  Eye,
  Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TabelleDieteticheModal({ open, onOpenChange }) {
  const { token, hasPermission } = useAuth();
  const [tabelle, setTabelle] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Form upload
  const [uploadData, setUploadData] = useState({
    nome: "",
    descrizione: "",
    vidimazione_asl: "",
    data_vidimazione: "",
    file: null
  });
  const [uploading, setUploading] = useState(false);

  const canUpload = hasPermission("struttura:update");
  const canDelete = hasPermission("fascicolo:delete"); // super_admin

  const fetchTabelle = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/tabelle-dietetiche`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTabelle(response.data.tabelle || []);
    } catch (error) {
      console.error("Errore caricamento tabelle:", error);
      toast.error("Errore nel caricamento delle tabelle");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchTabelle();
    }
  }, [open, fetchTabelle]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Verifica tipo
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Tipo file non supportato. Usa PDF, JPG o PNG.");
        return;
      }
      // Verifica dimensione (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File troppo grande (max 50MB)");
        return;
      }
      setUploadData(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error("Seleziona un file");
      return;
    }
    if (!uploadData.nome.trim()) {
      toast.error("Inserisci un nome per la tabella");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadData.file);
      formData.append("nome", uploadData.nome);
      formData.append("descrizione", uploadData.descrizione);
      formData.append("vidimazione_asl", uploadData.vidimazione_asl);
      formData.append("data_vidimazione", uploadData.data_vidimazione);

      await axios.post(
        `${API_URL}/api/tabelle-dietetiche`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      toast.success("Tabella dietetica caricata");
      setShowUploadForm(false);
      setUploadData({
        nome: "",
        descrizione: "",
        vidimazione_asl: "",
        data_vidimazione: "",
        file: null
      });
      fetchTabelle();
    } catch (error) {
      console.error("Errore upload:", error);
      toast.error(error.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await axios.delete(
        `${API_URL}/api/tabelle-dietetiche/${deleteConfirm.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Tabella eliminata");
      setDeleteConfirm(null);
      fetchTabelle();
    } catch (error) {
      console.error("Errore eliminazione:", error);
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleDownload = async (tabella) => {
    try {
      const { data } = await axios.get(`${API_URL}${tabella.file_url}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = tabella.file_url?.split("/").pop() || tabella.nome?.replace(/\s+/g, "_") || "tabella-dietetica";
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Errore download:", err);
      toast.error("Impossibile scaricare il file");
    }
  };

  const handlePreview = async (tabella) => {
    try {
      const { data } = await axios.get(`${API_URL}${tabella.file_url}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      setPreviewUrl(URL.createObjectURL(data));
    } catch (err) {
      console.error("Errore anteprima:", err);
      toast.error("Impossibile aprire l'anteprima");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/D";
    try {
      return new Date(dateStr).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "long",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/D";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type) => {
    if (type?.includes("pdf")) return FileText;
    if (type?.includes("image")) return Image;
    return FileText;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Utensils className="w-5 h-5 text-orange-600" />
              Tabelle Dietetiche
            </DialogTitle>
            <DialogDescription>
              Gestione delle tabelle dietetiche della struttura vidiimate dall'ASL
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 pb-4 border-b">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchTabelle}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
                Aggiorna
              </Button>

              {canUpload && (
                <Button
                  onClick={() => setShowUploadForm(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Carica Tabella
                </Button>
              )}
            </div>

            {/* Form Upload */}
            {showUploadForm && (
              <Card className="mt-4 border-orange-200 bg-orange-50/50">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-orange-800">Carica Nuova Tabella Dietetica</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowUploadForm(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nome tabella *</Label>
                      <Input
                        value={uploadData.nome}
                        onChange={(e) => setUploadData(prev => ({ ...prev, nome: e.target.value }))}
                        placeholder="Es: Tabella Dietetica 2026"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>ASL di vidimazione</Label>
                      <Input
                        value={uploadData.vidimazione_asl}
                        onChange={(e) => setUploadData(prev => ({ ...prev, vidimazione_asl: e.target.value }))}
                        placeholder="Es: ASL Roma 1"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data vidimazione</Label>
                      <Input
                        type="date"
                        value={uploadData.data_vidimazione}
                        onChange={(e) => setUploadData(prev => ({ ...prev, data_vidimazione: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>File (PDF, JPG, PNG) *</Label>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Descrizione</Label>
                    <Textarea
                      value={uploadData.descrizione}
                      onChange={(e) => setUploadData(prev => ({ ...prev, descrizione: e.target.value }))}
                      placeholder="Note aggiuntive sulla tabella dietetica..."
                      rows={2}
                      className="mt-1 resize-none"
                    />
                  </div>

                  {uploadData.file && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 rounded px-3 py-2">
                      <FileText className="w-4 h-4" />
                      <span>{uploadData.file.name}</span>
                      <span className="text-slate-400">({formatFileSize(uploadData.file.size)})</span>
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowUploadForm(false)}>
                      Annulla
                    </Button>
                    <Button
                      onClick={handleUpload}
                      disabled={uploading || !uploadData.file || !uploadData.nome.trim()}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {uploading ? "Caricamento..." : "Carica Tabella"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lista Tabelle */}
            <div className="flex-1 overflow-y-auto mt-4 space-y-3">
              {loading ? (
                <div className="text-center py-8 text-slate-500">
                  <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                  Caricamento...
                </div>
              ) : tabelle.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Utensils className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nessuna tabella dietetica caricata</p>
                  {canUpload && (
                    <Button
                      onClick={() => setShowUploadForm(true)}
                      variant="outline"
                      className="mt-4"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Carica la prima tabella
                    </Button>
                  )}
                </div>
              ) : (
                tabelle.map((tabella) => {
                  const FileIcon = getFileIcon(tabella.file_type);

                  return (
                    <Card 
                      key={tabella.id} 
                      className={`card-base ${tabella.attiva ? "border-orange-300 bg-orange-50/30" : ""}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Icona file */}
                          <div className={`p-3 rounded-lg ${tabella.attiva ? "bg-orange-100" : "bg-slate-100"}`}>
                            <FileIcon className={`w-6 h-6 ${tabella.attiva ? "text-orange-600" : "text-slate-500"}`} />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-900">{tabella.nome}</h4>
                              {tabella.attiva && (
                                <Badge className="bg-orange-500 text-white text-xs">
                                  <Check className="w-3 h-3 mr-1" />
                                  Attiva
                                </Badge>
                              )}
                            </div>

                            {tabella.descrizione && (
                              <p className="text-sm text-slate-600 mb-2">{tabella.descrizione}</p>
                            )}

                            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                              {tabella.vidimazione_asl && (
                                <span className="flex items-center gap-1">
                                  <Shield className="w-3 h-3" />
                                  {tabella.vidimazione_asl}
                                </span>
                              )}
                              {tabella.data_vidimazione && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Vidimata: {formatDate(tabella.data_vidimazione)}
                                </span>
                              )}
                              <span>{formatFileSize(tabella.file_size)}</span>
                            </div>

                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                              <User className="w-3 h-3" />
                              <span>Caricata da {tabella.uploadedByName} il {formatDate(tabella.uploadedAt)}</span>
                            </div>
                          </div>

                          {/* Azioni */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(tabella)}
                              title="Visualizza"
                            >
                              <Eye className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(tabella)}
                              title="Scarica"
                            >
                              <Download className="w-4 h-4 text-slate-600" />
                            </Button>
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteConfirm(tabella)}
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
        <DialogContent className="max-w-5xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle>Anteprima Tabella Dietetica</DialogTitle>
            <DialogDescription>Visualizzazione del file caricato</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {previewUrl && (
              <iframe
                src={`${previewUrl}#toolbar=0`}
                className="w-full h-[70vh] border rounded"
                title="Anteprima"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Dialog Conferma Eliminazione */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Conferma Eliminazione
            </AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente la tabella dietetica "{deleteConfirm?.nome}".
              <br />
              <strong>Questa azione non può essere annullata.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Elimina Definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default TabelleDieteticheModal;
